import React, {createContext, useCallback, useContext, useEffect, useState} from "react";
import {RouterProvider} from "react-router-dom";
import Router from './routes';
import {LanguagesType} from "./types/Languages";
import {ColorModesType} from "./types/ColorModes";
import {ContextType} from "./types/Context";
import i18n from "./lang/i18n";
import {useMediaQuery} from "react-responsive";
import StorageConstants from "../src/constants/LocalStorageKeys";
import GlobalSpinner from "./components/GlobalSpinner";
import {Trip} from "./types/Trip";
import {Toast} from "./types/General";

// @ts-ignore
const body: HTMLHtmlElement = document.querySelector('html');

// @ts-ignore
const ManageContext: React.Context<ContextType> = createContext({
    trip: [],
    setTrip: () => {},
    lang: LanguagesType.English,
    switchLang: () => {},
    colorMode: ColorModesType.auto,
    switchTheme: () => {},
    isDarkMode: false,
    toast: null,
    setToast: () => {},
});

export const useManageContext = () => useContext(ManageContext);

export default function App() {

    // @ts-ignore
    const initialLang: LanguagesType = localStorage.getItem(StorageConstants.lang) ?? LanguagesType.English;
    const [lang, setLang] = useState<LanguagesType>(initialLang);
    // @ts-ignore
    const initialColorMode: ColorModesType = localStorage.getItem(StorageConstants.colorMode) ?? ColorModesType.light;
    const [colorMode, setColorMode] = useState<ColorModesType>(
        initialColorMode === ColorModesType.auto && window.matchMedia('(prefers-color-scheme: dark)').matches
            ? ColorModesType.dark
            : initialColorMode
    );
    const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [trip, setTrip] = useState<Trip | null>(null);
    const [toast, setToast] = useState<Toast | null>(null);

    const switchTheme = useCallback((newColorMode: ColorModesType, isSystemOriginatedChange: boolean = false) => {
        // Skip is the change was originated by the system but the color mode is not auto
        if (isSystemOriginatedChange && colorMode !== ColorModesType.auto && isSystemOriginatedChange) {
            return;
        }

        if (!isSystemOriginatedChange) {
            setColorMode(newColorMode);
            // Store it
            localStorage.setItem(StorageConstants.colorMode, newColorMode);
        }

        const isDark = newColorMode === ColorModesType.dark || (newColorMode === ColorModesType.auto && window.matchMedia('(prefers-color-scheme: dark)').matches);

        // @ts-ignore
        body.dataset.bsTheme = isDark ? ColorModesType.dark : ColorModesType.light;
        setIsDarkMode(isDark);
    }, [colorMode, setColorMode]);

    const switchLang = useCallback((newLang: LanguagesType) => {
        setLang(newLang);
        i18n.locale = newLang;
        localStorage.setItem(StorageConstants.lang, newLang);
    }, [setLang]);

    useEffect(() => {
        switchTheme(initialColorMode);
        switchLang(initialLang);
        setIsLoading(false);
    }, [initialColorMode, initialLang, switchLang, switchTheme]);

    useMediaQuery({ query: "(prefers-color-scheme: dark)"},
        undefined,
        (isSystemDark) => switchTheme(isSystemDark ? ColorModesType.dark : ColorModesType.light, true)
    );

    if (isLoading) {
        return <div style={{height: '100vh'}} className="d-flex position-relative justify-content-center align-items-center">
            <GlobalSpinner/>
        </div>;
    }

    return (
        <ManageContext.Provider value={{
            trip,
            setTrip,
            lang,
            switchLang,
            colorMode,
            switchTheme,
            isDarkMode,
            toast,
            setToast,
        }}>
            <RouterProvider router={Router}/>
        </ManageContext.Provider>
    );
}